import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Controller } from 'src/components/form/field-controller/Controller';
import { FormField } from 'src/components/form/form/FormField';
import Select from 'src/components/form/select';

import type { AssignmentRule } from './assignmentRule';
import { ASSIGNMENT_RULES } from './assignmentRule';
import type { ScheduleFormFieldData } from './scheduleFormSchema';

/**
 * One choice for the whole schedule. Each occurrence resolves it against
 * whoever owns the object at the time, so ownership changes reach future
 * occurrences without anyone editing the schedule. A minted task keeps the
 * people it was minted with.
 */
export const AssignmentField: FC = () => {
  const { t } = useTranslation(['common']);
  const { control } = useFormContext<ScheduleFormFieldData>();
  const { error } = control.getFieldState('Assignment');

  const options = useMemo(
    () =>
      ASSIGNMENT_RULES.map((rule) => ({
        value: rule,
        label: t(`scheduler.assignment_options.${rule}`),
      })),
    [t]
  );

  return (
    <Controller
      name={'Assignment'}
      control={control}
      render={({ field: { ref, onChange, onBlur, value } }) => (
        <FormField
          label={t('scheduler.fields.assignment')}
          description={t('scheduler.fields.assignment_description')}
          errorText={error?.message}
          stretch={true}
          testId={'schedule-assignment'}
        >
          <Select
            ref={ref}
            options={options}
            selectedOption={
              options.find((option) => option.value === value) ?? null
            }
            onBlur={onBlur}
            onChange={(e) => {
              const selected = options.find(
                (option) => option.value === e.detail.selectedOption?.value
              );
              if (selected) {
                onChange(selected.value satisfies AssignmentRule);
              }
            }}
            placeholder={t('select')}
          />
        </FormField>
      )}
    />
  );
};
